import {DEFAULT_BEHAVIOR} from '../../constants/defaultBehavior';
import {BehaviorResponseBody} from '../../types/Api';
import type {EagGridState} from '../createEagGridStore';
import {useEagGridSelector} from '../useEagGridSelector';

/**
 * Module-level selector functions for behavior flags.
 *
 * Each selector returns a primitive value (boolean, string, enum) so
 * useSyncExternalStore can compare with Object.is() — stable references
 * when the underlying definition.behavior hasn't changed.
 *
 * All selectors fall back to DEFAULT_BEHAVIOR when definition is not loaded.
 */

export const selectBehavior = (state: EagGridState): BehaviorResponseBody =>
  state.definition?.behavior ?? DEFAULT_BEHAVIOR;

export const selectSmartSearchEnabled = (state: EagGridState): boolean =>
  selectBehavior(state).smartSearchEnabled;

export const selectPresetsEnabled = (state: EagGridState): boolean =>
  selectBehavior(state).presetsEnabled;

export const selectGlobalActionsEnabled = (state: EagGridState): boolean =>
  selectBehavior(state).globalActionsEnabled;

export const selectFilterToolPanelVisible = (state: EagGridState): boolean =>
  selectBehavior(state).filterToolPanelVisible;

export const selectColumnToolPanelVisible = (state: EagGridState): boolean =>
  selectBehavior(state).columnToolPanelVisible;

export const selectSettingsToolPanelVisible = (state: EagGridState): boolean =>
  selectBehavior(state).settingsToolPanelVisible;

export const selectColumnMovingAllowed = (state: EagGridState): boolean =>
  selectBehavior(state).columnMovingAllowed;

export const selectColumnResizingAllowed = (state: EagGridState): boolean =>
  selectBehavior(state).columnResizingAllowed;

export const selectColumnSortMode = (state: EagGridState): 'SINGLE_SORT' | 'MULTI_SORT' | 'NONE' =>
  selectBehavior(state).columnSortMode;

export const selectColumnMenuEnabled = (state: EagGridState): boolean =>
  selectBehavior(state).columnMenuEnabled;

export const selectColumnPinningAllowed = (state: EagGridState): boolean =>
  selectBehavior(state).columnPinningAllowed;

export const selectColumnAutoSizingAllowed = (state: EagGridState): boolean =>
  selectBehavior(state).columnAutoSizingAllowed;

export const selectColumnHidingAllowed = (state: EagGridState): boolean =>
  selectBehavior(state).columnHidingAllowed;

export const selectActionColumnEnabled = (state: EagGridState): boolean =>
  selectBehavior(state).actionColumnEnabled;

export const selectPaginationEnabled = (state: EagGridState): boolean =>
  selectBehavior(state).paginationEnabled;

export const selectRowSelectMode = (state: EagGridState): 'SINGLE' | 'MULTIPLE' | 'NONE' =>
  selectBehavior(state).rowSelectMode;

export const selectWrapperStyle = (state: EagGridState): 'CARD' | 'INLINE' =>
  selectBehavior(state).wrapperStyle;

export const selectFilterMode = (state: EagGridState): 'NATIVE' | 'CUSTOM' =>
  selectBehavior(state).filterMode;

export const selectClipboardExportAllowed = (state: EagGridState): boolean =>
  selectBehavior(state).clipboardExportAllowed;

/**
 * Hook wrappers for component use.
 *
 * These hooks subscribe the component to the specific behavior flag via
 * useEagGridSelector + useSyncExternalStore. The component will re-render
 * only when that specific flag changes.
 */

export function useSmartSearchEnabled(): boolean {
  return useEagGridSelector(selectSmartSearchEnabled);
}

export function usePresetsEnabled(): boolean {
  return useEagGridSelector(selectPresetsEnabled);
}

export function useGlobalActionsEnabled(): boolean {
  return useEagGridSelector(selectGlobalActionsEnabled);
}

export function useFilterToolPanelVisible(): boolean {
  return useEagGridSelector(selectFilterToolPanelVisible);
}

export function useColumnToolPanelVisible(): boolean {
  return useEagGridSelector(selectColumnToolPanelVisible);
}

export function useSettingsToolPanelVisible(): boolean {
  return useEagGridSelector(selectSettingsToolPanelVisible);
}

export function useColumnMovingAllowed(): boolean {
  return useEagGridSelector(selectColumnMovingAllowed);
}

export function useColumnResizingAllowed(): boolean {
  return useEagGridSelector(selectColumnResizingAllowed);
}

export function useColumnSortMode(): 'SINGLE_SORT' | 'MULTI_SORT' | 'NONE' {
  return useEagGridSelector(selectColumnSortMode);
}

export function useColumnMenuEnabled(): boolean {
  return useEagGridSelector(selectColumnMenuEnabled);
}

export function useColumnPinningAllowed(): boolean {
  return useEagGridSelector(selectColumnPinningAllowed);
}

export function useColumnAutoSizingAllowed(): boolean {
  return useEagGridSelector(selectColumnAutoSizingAllowed);
}

export function useColumnHidingAllowed(): boolean {
  return useEagGridSelector(selectColumnHidingAllowed);
}

export function useActionColumnEnabled(): boolean {
  return useEagGridSelector(selectActionColumnEnabled);
}

export function usePaginationEnabled(): boolean {
  return useEagGridSelector(selectPaginationEnabled);
}

export function useClipboardExportAllowed(): boolean {
  return useEagGridSelector(selectClipboardExportAllowed);
}
